import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Loader2, Plus, Trash2, Download, CheckCircle2, Mail, FileText, Building2, ChevronDown, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function InvoicePreview({ fromCompany, billTo, invoiceDate, terms, selectedTruck, items, subtotal, serviceFee, taxRate, taxAmt, totalDue, notes, compact }) {
  const scale = compact ? 'text-xs' : 'text-sm';
  const pad = compact ? 'p-5' : 'p-8';
  const headerPad = compact ? 'px-6 py-4' : 'px-10 py-6';
  const tblPad = compact ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div className="bg-white rounded-sm shadow-2xl overflow-hidden" data-testid="invoice-preview">
      <div className={`bg-[#3B82F6] ${headerPad} flex items-center justify-between`}>
        <span className={`font-['Helvetica'] font-bold text-white ${compact ? 'text-xl' : 'text-3xl'} tracking-wide`}>INVOICE</span>
        <span className={`font-['Helvetica'] font-bold text-white ${compact ? 'text-sm' : 'text-lg'}`}>{fromCompany.name || 'Your Company'}</span>
      </div>

      <div className={`${pad} space-y-5`}>
        <div className={`grid grid-cols-3 gap-4 ${scale}`}>
          <div>
            <p className="font-bold text-gray-900">{fromCompany.name || '—'}</p>
            <p className="text-gray-500">{fromCompany.address || ''}</p>
            <p className="text-gray-500">{fromCompany.phone || ''}</p>
            <p className="text-gray-500">{fromCompany.email || ''}</p>
          </div>
          <div>
            <p className={`${compact ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-widest text-gray-400 font-bold mb-0.5`}>Bill To</p>
            <p className="font-bold text-gray-900">{billTo.name || '—'}</p>
            <p className="text-gray-500">{billTo.address || ''}</p>
            <p className="text-gray-500">{billTo.email || ''}</p>
          </div>
          <div className="text-right space-y-0.5">
            <div className="flex justify-end gap-2"><span className="text-gray-400">Date:</span><span className="text-gray-900">{invoiceDate}</span></div>
            <div className="flex justify-end gap-2"><span className="text-gray-400">Due:</span><span className="text-gray-900">{terms}</span></div>
            {selectedTruck && <div className="flex justify-end gap-2"><span className="text-gray-400">Unit:</span><span className="text-[#3B82F6] font-bold">{selectedTruck.unit_number}</span></div>}
          </div>
        </div>

        <div className="h-0.5 bg-[#3B82F6]"></div>

        <table className={`w-full ${scale}`}>
          <thead>
            <tr className="bg-[#0F172A] text-white">
              <th className={`text-left ${tblPad} font-['Helvetica'] font-bold ${compact ? 'text-[9px]' : 'text-[11px]'} uppercase`}>Description</th>
              <th className={`text-center ${tblPad} font-['Helvetica'] font-bold ${compact ? 'text-[9px]' : 'text-[11px]'} uppercase`}>Qty</th>
              <th className={`text-right ${tblPad} font-['Helvetica'] font-bold ${compact ? 'text-[9px]' : 'text-[11px]'} uppercase`}>Unit Price</th>
              <th className={`text-right ${tblPad} font-['Helvetica'] font-bold ${compact ? 'text-[9px]' : 'text-[11px]'} uppercase`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.filter(i => i.desc.trim()).map((item, idx) => (
              <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className={`${tblPad} text-gray-900`}>{item.desc}</td>
                <td className={`${tblPad} text-center text-gray-500`}>{item.qty}</td>
                <td className={`${tblPad} text-right text-gray-500`}>{fmt(item.unit_price)}</td>
                <td className={`${tblPad} text-right font-bold text-gray-900`}>{fmt((parseFloat(item.qty) || 0) * (parseFloat(item.unit_price) || 0))}</td>
              </tr>
            ))}
            {items.every(i => !i.desc.trim()) && (
              <tr><td colSpan={4} className={`${tblPad} text-center text-gray-300 italic`}>Add line items above...</td></tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className={`${compact ? 'w-56' : 'w-72'} space-y-1.5 ${scale}`}>
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="text-gray-900">{fmt(subtotal)}</span></div>
            {parseFloat(serviceFee) > 0 && <div className="flex justify-between"><span className="text-gray-500">Service Fee</span><span className="text-gray-900">{fmt(serviceFee)}</span></div>}
            {taxAmt > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({taxRate}%)</span><span className="text-gray-900">{fmt(taxAmt)}</span></div>}
            <div className={`flex justify-between pt-2 border-t-2 border-[#3B82F6]`}>
              <span className={`font-bold ${compact ? 'text-sm' : 'text-lg'} text-gray-900`}>TOTAL DUE</span>
              <span className={`font-bold ${compact ? 'text-sm' : 'text-lg'} text-gray-900`}>{fmt(totalDue)}</span>
            </div>
          </div>
        </div>

        {notes && (
          <div className="pt-3 border-t border-[#ddd]">
            <p className={`${compact ? 'text-[8px]' : 'text-[10px]'} uppercase tracking-widest text-[#999] font-bold mb-1`}>Notes</p>
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-[#555]`}>{notes}</p>
          </div>
        )}
      </div>

      <div className={`border-t border-[#0F172A] px-6 ${compact ? 'py-2' : 'py-3'}`}>
        <p className={`${compact ? 'text-[8px]' : 'text-[10px]'} text-[#999] text-center`}>Thank you for your business — {fromCompany.name} | {fromCompany.email} | {fromCompany.phone}</p>
      </div>
    </div>
  );
}

const TENANT_INFO = {
  MM: { name: 'Millennials Maintenance', phone: '', email: '', address: '' },
  MT: { name: 'MT Services', phone: '', email: '', address: '' },
};

const EMPTY_ITEM = { desc: '', qty: 1, unit_price: 0 };

export default function CreateInvoice() {
  const navigate = useNavigate();

  // Data
  const [customers, setCustomers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selections
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedTruckId, setSelectedTruckId] = useState('');
  const [selectedRepairId, setSelectedRepairId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Invoice header
  const [fromCompany, setFromCompany] = useState({ ...TENANT_INFO.MM });
  const [billTo, setBillTo] = useState({ name: '', address: '', email: '' });
  const [invoiceDate, setInvoiceDate] = useState(new Date().toLocaleDateString('en-US'));
  const [dueDays, setDueDays] = useState(7);
  const [terms, setTerms] = useState('Net 7');
  const [notes, setNotes] = useState('Thank you for your business. Payment is due upon receipt.');

  // Line items
  const [items, setItems] = useState([{ ...EMPTY_ITEM, id: 1 }]);
  const [nextId, setNextId] = useState(2);

  // Pricing
  const [serviceFee, setServiceFee] = useState(0);
  const [taxRate, setTaxRate] = useState(0);

  // State
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [custRes, tiersRes] = await Promise.all([
          api.get('/customers?limit=200'),
          api.get('/invoice-transformer/pricing-tiers'),
        ]);
        setCustomers(custRes.data.items || []);
        setTiers(tiersRes.data || []);
      } catch { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  // Load trucks when customer selected
  useEffect(() => {
    if (!selectedCustomerId) { setTrucks([]); setRepairs([]); return; }
    const loadRelated = async () => {
      try {
        const [tRes, rRes] = await Promise.all([
          api.get('/trucks', { params: { customer_id: selectedCustomerId } }),
          api.get('/repairs', { params: { customer_id: selectedCustomerId, limit: 50 } }),
        ]);
        setTrucks(tRes.data || []);
        setRepairs(rRes.data.items || []);
      } catch {}
    };
    loadRelated();
  }, [selectedCustomerId]);

  const selectCustomer = (cust) => {
    setSelectedCustomerId(cust.id);
    setCustomerSearch(cust.company_name);
    setShowCustomerDropdown(false);
    setSelectedTruckId('');
    setSelectedRepairId('');

    // Auto-fill company info based on tenant
    const tenant = cust.tenant_group || 'MM';
    setFromCompany({ ...TENANT_INFO[tenant] || TENANT_INFO.MM });
    setBillTo({
      name: cust.company_name || '',
      address: cust.address || '',
      email: cust.contact_email || '',
    });
  };

  // Computed
  const subtotal = items.reduce((a, i) => a + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const taxAmt = Math.round(subtotal * (parseFloat(taxRate) / 100) * 100) / 100;
  const totalDue = Math.round((subtotal + parseFloat(serviceFee || 0) + taxAmt) * 100) / 100;

  const addItem = () => {
    setItems(prev => [...prev, { ...EMPTY_ITEM, id: nextId }]);
    setNextId(n => n + 1);
  };

  const removeItem = (id) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const handleGenerate = async () => {
    if (!selectedCustomerId) return toast.error('Select a customer');
    if (items.every(i => !i.desc.trim())) return toast.error('Add at least one line item');

    setGenerating(true);
    try {
      const payload = {
        customer_id: selectedCustomerId,
        truck_id: selectedTruckId || undefined,
        repair_id: selectedRepairId || undefined,
        from_company: fromCompany,
        bill_to: billTo,
        invoice_date: invoiceDate,
        due_days: parseInt(dueDays),
        terms,
        notes,
        items: items.filter(i => i.desc.trim()).map(i => ({
          desc: i.desc,
          qty: parseFloat(i.qty) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
          line_total: Math.round((parseFloat(i.qty) || 1) * (parseFloat(i.unit_price) || 0) * 100) / 100,
        })),
        service_fee: parseFloat(serviceFee) || 0,
        tax_rate: parseFloat(taxRate) || 0,
      };
      const res = await api.post('/invoice-transformer/manual-invoice', payload);
      setGenerated(res.data);
      toast.success('Invoice generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
  const selectedTruck = trucks.find(t => t.id === selectedTruckId);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;

  // ═══ SUCCESS STATE ═══
  if (generated) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center animate-fade-in" data-testid="invoice-success-view">
        <div className="w-20 h-20 rounded-full bg-[#57EB31]/10 border-2 border-[#57EB31]/40 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-[#57EB31]" />
        </div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#5C6A7A] mb-2">// INVOICE CREATED</p>
        <h2 className="text-white font-['Manrope'] font-extrabold text-3xl mb-2">{generated.invoice_number}</h2>
        <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] mb-1">Total Due: <strong className="text-[#3B82F6] text-xl">{fmt(generated.totals?.total_due)}</strong></p>
        <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mb-8">Billed to: {generated.bill_to?.name}</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <a href={`${BACKEND}${generated.pdf_url}?token=${localStorage.getItem('mm_token')}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-[#57EB31] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#57EB31]/90 transition-colors"
            data-testid="download-created-pdf">
            <Download size={16} /> Download PDF
          </a>
          <button onClick={async () => {
              setEmailing(true);
              try {
                await api.post(`/invoice-transformer/customer-invoices/${generated.id}/send-email`, {});
                toast.success(`Invoice emailed to ${generated.bill_to?.email}`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
              finally { setEmailing(false); }
            }} disabled={emailing}
            className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors disabled:opacity-50"
            data-testid="email-created-invoice-btn">
            {emailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {emailing ? 'Sending...' : 'Email to Customer'}
          </button>
          <button onClick={() => { setGenerated(null); setItems([{ ...EMPTY_ITEM, id: 1 }]); setNextId(2); }}
            className="flex items-center gap-2 px-6 py-3 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors"
            data-testid="create-another-btn">
            <Plus size={16} /> Create Another
          </button>
        </div>
      </div>
    );
  }

  // ═══ MAIN BUILDER ═══
  return (
    <div className="p-6 animate-fade-in" data-testid="create-invoice-page">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#5C6A7A] mb-1">// CREATE INVOICE</p>
        <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Invoice Builder</h1>
        <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mt-1">Build a professional invoice from scratch</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* ═══ LEFT COLUMN — Form ═══ */}
        <div className="col-span-5 space-y-5">

          {/* Customer Selection */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5" data-testid="customer-selection-section">
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={14} className="text-[#3B82F6]" />
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">Select Customer</p>
            </div>
            <div className="relative">
              <input
                type="text"
                value={customerSearch}
                onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) setSelectedCustomerId(''); }}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Type to search customers..."
                className="w-full h-11 px-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="customer-search-input"
              />
              {showCustomerDropdown && customerSearch && (
                <div className="absolute z-50 w-full mt-1 bg-[#1A2332] border border-[#2D3F5E] rounded-sm max-h-60 overflow-y-auto shadow-2xl" data-testid="customer-dropdown">
                  {filteredCustomers.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-[#5C6A7A] font-['IBM_Plex_Sans']">No customers found</p>
                  ) : filteredCustomers.slice(0, 15).map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[#3B82F6]/5 transition-colors border-b border-[#2D3F5E]/30 last:border-0 ${selectedCustomerId === c.id ? 'bg-[#3B82F6]/10' : ''}`}
                      data-testid={`customer-option-${c.id}`}>
                      <div>
                        <p className="text-sm font-['IBM_Plex_Sans'] font-medium text-white">{c.company_name}</p>
                        <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans']">{c.contact_email}</p>
                      </div>
                      <span className={`text-[9px] font-['JetBrains_Mono'] font-bold px-2 py-0.5 rounded-sm border ${c.tenant_group === 'MT' ? 'text-violet-400 bg-violet-900/20 border-violet-700/50' : 'text-emerald-400 bg-emerald-900/20 border-emerald-700/50'}`}>
                        {c.tenant_group || 'MM'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedCustomer && (
              <div className="mt-3 p-3 rounded-sm bg-[#0A0F1A] border border-[#3B82F6]/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-['IBM_Plex_Sans'] font-bold text-[#3B82F6]">{selectedCustomer.company_name}</p>
                  <span className={`text-[9px] font-['JetBrains_Mono'] font-bold px-2 py-0.5 rounded-sm border ${selectedCustomer.tenant_group === 'MT' ? 'text-violet-400 bg-violet-900/20 border-violet-700/50' : 'text-emerald-400 bg-emerald-900/20 border-emerald-700/50'}`}>
                    {selectedCustomer.tenant_group || 'MM'}
                  </span>
                </div>
                <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-0.5">{selectedCustomer.contact_email}</p>
              </div>
            )}
          </div>

          {/* Unit & Repair (optional) */}
          {selectedCustomerId && (
            <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5 space-y-3" data-testid="unit-repair-section">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">Link to Unit / Repair (Optional)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Unit</label>
                  <select value={selectedTruckId} onChange={e => setSelectedTruckId(e.target.value)}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]"
                    data-testid="unit-select">
                    <option value="">None</option>
                    {trucks.map(t => (
                      <option key={t.id} value={t.id}>{t.unit_number} — {t.year} {t.make} {t.model}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Repair</label>
                  <select value={selectedRepairId} onChange={e => setSelectedRepairId(e.target.value)}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]"
                    data-testid="repair-select">
                    <option value="">None</option>
                    {repairs.map(r => (
                      <option key={r.id} value={r.id}>{r.truck_unit_number} — {r.issue_description}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* From Company */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">From (Your Company)</p>
            {[['Company Name', 'name'], ['Address', 'address'], ['Phone', 'phone'], ['Email', 'email']].map(([label, field]) => (
              <div key={field}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{label}</label>
                <input type="text" value={fromCompany[field]} onChange={e => setFromCompany(p => ({ ...p, [field]: e.target.value }))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            ))}
          </div>

          {/* Bill To */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">Bill To</p>
            {[['Client Name', 'name'], ['Address', 'address'], ['Email', 'email']].map(([label, field]) => (
              <div key={field}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{label}</label>
                <input type="text" value={billTo[field]} onChange={e => setBillTo(p => ({ ...p, [field]: e.target.value }))}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            ))}
          </div>

          {/* Invoice Details */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5 space-y-3">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">Invoice Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Invoice Date</label>
                <input type="text" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Net Days</label>
                <input type="number" value={dueDays} onChange={e => { setDueDays(e.target.value); setTerms(`Net ${e.target.value}`); }}
                  className="w-full h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Notes / Payment Instructions</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6] resize-none" />
            </div>
          </div>
        </div>

        {/* ═══ RIGHT COLUMN — Line Items + Preview ═══ */}
        <div className="col-span-7 space-y-5">

          {/* Line Items */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5" data-testid="line-items-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-[#3B82F6]" />
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A]">Line Items</p>
              </div>
              <button onClick={addItem}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']"
                data-testid="add-line-item-btn">
                <Plus size={12} /> Add Item
              </button>
            </div>

            {/* Header row */}
            <div className="grid grid-cols-12 gap-2 mb-2 px-1">
              <span className="col-span-6 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Description</span>
              <span className="col-span-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] text-center">Qty</span>
              <span className="col-span-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] text-right">Unit Price</span>
              <span className="col-span-1 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] text-right">Total</span>
              <span className="col-span-1"></span>
            </div>

            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.id} className="grid grid-cols-12 gap-2 items-center p-2 rounded-sm bg-[#0A0F1A] border border-[#2D3F5E]/60 hover:border-[#2D3F5E] transition-colors"
                  data-testid={`line-item-${idx}`}>
                  <input
                    className="col-span-6 h-8 px-3 bg-transparent border border-[#2D3F5E]/40 rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
                    value={item.desc}
                    onChange={e => updateItem(item.id, 'desc', e.target.value)}
                    placeholder="Part description..."
                    data-testid={`item-desc-${idx}`}
                  />
                  <input
                    className="col-span-2 h-8 px-2 bg-transparent border border-[#2D3F5E]/40 rounded-sm text-white font-['JetBrains_Mono'] text-sm text-center focus:outline-none focus:border-[#3B82F6] transition-colors"
                    type="number" step="1" min="1"
                    value={item.qty}
                    onChange={e => updateItem(item.id, 'qty', e.target.value)}
                    data-testid={`item-qty-${idx}`}
                  />
                  <div className="col-span-2 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#5C6A7A] font-['JetBrains_Mono'] text-xs">$</span>
                    <input
                      className="w-full h-8 pl-5 pr-2 bg-transparent border border-[#2D3F5E]/40 rounded-sm text-white font-['JetBrains_Mono'] text-sm text-right focus:outline-none focus:border-[#3B82F6] transition-colors"
                      type="number" step="0.01" min="0"
                      value={item.unit_price}
                      onChange={e => updateItem(item.id, 'unit_price', e.target.value)}
                      data-testid={`item-price-${idx}`}
                    />
                  </div>
                  <span className="col-span-1 text-right text-sm font-['JetBrains_Mono'] font-bold text-[#3B82F6]">
                    {fmt((parseFloat(item.qty) || 0) * (parseFloat(item.unit_price) || 0))}
                  </span>
                  <div className="col-span-1 flex justify-center">
                    <button onClick={() => removeItem(item.id)} disabled={items.length <= 1}
                      className="p-1.5 rounded-sm text-[#2D3F5E] hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                      data-testid={`remove-item-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fees & Tax */}
          <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-4">Fees & Tax</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Service Fee ($)</label>
                <div className="flex items-center gap-2">
                  <span className="text-[#3B82F6] font-['JetBrains_Mono'] font-bold">$</span>
                  <input type="number" step="0.01" min="0" value={serviceFee} onChange={e => setServiceFee(e.target.value)}
                    className="flex-1 h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]"
                    data-testid="manual-service-fee" />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[0, 50, 75, 100, 150].map(v => (
                    <button key={v} type="button" onClick={() => setServiceFee(v)}
                      className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] rounded-sm border transition-all ${parseFloat(serviceFee) === v ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'border-[#2D3F5E] text-[#5C6A7A] hover:border-[#3B82F6]/40'}`}>
                      ${v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Tax Rate (%)</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="0.01" min="0" value={taxRate} onChange={e => setTaxRate(e.target.value)}
                    className="w-20 h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]"
                    data-testid="manual-tax-rate" />
                  <span className="text-[#8B9AAB] font-['JetBrains_Mono']">%</span>
                  <span className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">= {fmt(taxAmt)}</span>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[0, 5, 7, 8.25, 10].map(v => (
                    <button key={v} type="button" onClick={() => setTaxRate(v)}
                      className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] rounded-sm border transition-all ${parseFloat(taxRate) === v ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'border-[#2D3F5E] text-[#5C6A7A] hover:border-[#3B82F6]/40'}`}>
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ═══ LIVE PREVIEW ═══ */}
          <div className="relative group cursor-pointer" onClick={() => setPreviewFullscreen(true)} data-testid="invoice-preview-wrapper">
            <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-sm">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm shadow-lg">
                <Maximize2 size={16} /> Full Page Preview
              </div>
            </div>
            <InvoicePreview
              fromCompany={fromCompany} billTo={billTo} invoiceDate={invoiceDate}
              terms={terms} selectedTruck={selectedTruck} items={items}
              subtotal={subtotal} serviceFee={serviceFee} taxRate={taxRate}
              taxAmt={taxAmt} totalDue={totalDue} notes={notes} compact
            />
          </div>

          {/* Fullscreen Preview Modal */}
          {previewFullscreen && (
            <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" onClick={() => setPreviewFullscreen(false)} data-testid="preview-fullscreen-modal">
              <div className="relative w-full max-w-[850px]" onClick={e => e.stopPropagation()}>
                <button onClick={() => setPreviewFullscreen(false)}
                  className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-[#1A2332] border border-[#2D3F5E] rounded-full flex items-center justify-center text-[#8B9AAB] hover:text-white hover:border-[#3B82F6] transition-colors shadow-xl"
                  data-testid="close-fullscreen-btn">
                  <X size={18} />
                </button>
                <InvoicePreview
                  fromCompany={fromCompany} billTo={billTo} invoiceDate={invoiceDate}
                  terms={terms} selectedTruck={selectedTruck} items={items}
                  subtotal={subtotal} serviceFee={serviceFee} taxRate={taxRate}
                  taxAmt={taxAmt} totalDue={totalDue} notes={notes}
                />
              </div>
            </div>
          )}

          {/* Generate Button */}
          <button onClick={handleGenerate} disabled={generating || !selectedCustomerId}
            className="w-full h-14 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(59,130,246,0.2)] flex items-center justify-center gap-3"
            data-testid="generate-invoice-btn">
            {generating ? <><Loader2 size={18} className="animate-spin" /> Generating PDF...</> : <><Download size={18} /> Generate Invoice PDF</>}
          </button>
        </div>
      </div>
    </div>
  );
}
